

import { useCallback, useState } from 'react';
import { API_ORIGIN } from '../utils/homeHelpers';
import {
  _computeStreak,
  _computeStreakFromDays,
  _loadTriviaHistory,
  _mergeRecentIntoLocalHistory,
} from '../utils/triviaHelpers';
import {
  _loadUserStatsCache,
  _saveUserStatsCache,
} from '../utils/userStatsCache';

const useUserStats = ({ resolvedUid }) => {
  const [userStats, setUserStats] = useState(() => _loadUserStatsCache(resolvedUid));

  const fetchUserStats = useCallback(async (force = false) => {
    // ⛔ DEV SAFEGUARD:
    // En desarrollo limitamos la frecuencia para evitar spam hacia /api/trivia/user-stats
    // causado por StrictMode, remounts, visibilidad de pestaña, etc.
    // PERO si `force` es true, saltamos este guard para tener datos frescos "ya".
    if (process.env.NODE_ENV !== 'production' && !force) {
      const now = Date.now();
      const last = window.__lastUserStatsFetchDev || 0;
      // si pedimos hace < 5000ms (~5s), no volvemos a pegarle al backend
      if (now - last < 5000) {
        // simplemente salimos SIN tocar estado, para evitar loops infinitos
        return;
      }
      window.__lastUserStatsFetchDev = now;
    } else if (process.env.NODE_ENV !== 'production' && force) {
      // Aún marcamos el timestamp para no disparar demasiadas seguidas, pero no bloqueamos
      window.__lastUserStatsFetchDev = Date.now();
    }

    if (!resolvedUid) {
      setUserStats(null);
      return;
    }

    try {
      const url = `${API_ORIGIN}/api/trivia/user-stats?userId=${encodeURIComponent(resolvedUid)}`;
      const res = await fetch(url, { credentials: 'include' });

      if (res.status === 404) {
        console.warn('Sin estadísticas previas: se crearán al primer juego.');
        setUserStats(null);
        return;
      }

      if (!res.ok) throw new Error(`Error HTTP ${res.status}`);

      const raw = await res.json();

      // Accept two formats: (1) flat; (2) { ok, stats: {...} }
      const s = (raw && typeof raw === 'object' && raw.stats && typeof raw.stats === 'object')
        ? raw.stats
        : raw;

      // Derive best score if not explicit (use recent arrays)
      const recentA = Array.isArray(s.last7Days) ? s.last7Days : [];
      const recentB = Array.isArray(s.recent) ? s.recent : [];
      const recent = recentA.length ? recentA : recentB;

      const bestFromRecent = recent.reduce((m, r) => {
        const val = Number(r?.score ?? 0);
        return Number.isFinite(val) ? Math.max(m, val) : m;
      }, 0);

      // Streak inferred from recent days
      const recentDays = (Array.isArray(recent) ? recent : [])
        .filter((x) => Number(x?.score || 0) > 0)
        .map((x) => String(x?.day || ''))
        .filter(Boolean);

      const recentStreak = _computeStreakFromDays(recentDays);

      // Align local history with backend recent days
      try {
        _mergeRecentIntoLocalHistory(
          (Array.isArray(recent) ? recent : []).filter((x) => Number(x?.score || 0) > 0),
          resolvedUid
        );
      } catch {}

      const todayScore = Number(s.todayScore ?? s.lastScore ?? 0);
      const totalScoreMaybe = Number(s.totalScore ?? s.sumScore ?? s.total ?? 0);
      const gamesMaybe = Number(s.totalGames ?? s.gamesPlayed ?? s.daysPlayed ?? s.totalDays ?? s.totalDaysPlayed ?? 0);
      const avgMaybe = Number(s.avgScore ?? s.averageScore ?? s.average);

      // Fallbacks for totalScore if backend doesn't send directly
      const totalScoreFinal = (() => {
        if (Number.isFinite(totalScoreMaybe) && totalScoreMaybe > 0) return totalScoreMaybe;
        if (Number.isFinite(avgMaybe) && gamesMaybe > 0) return Math.round(avgMaybe * gamesMaybe);

        const sumRecent = recent.reduce((acc, r) => acc + (Number(r?.score) || 0), 0);
        if (sumRecent > 0) return sumRecent;

        return 0;
      })();

      const inferredBestFromTotals = (Number.isFinite(totalScoreFinal) && gamesMaybe > 0)
        ? Math.max(1, Math.round(totalScoreFinal / gamesMaybe))
        : 0;

      const normalized = {
        // Current streak
        streak: Number(s.currentStreak ?? s.streak ?? s.bestStreak ?? 0),
        // Best score
        best: Number(
          s.bestScore ?? s.maxScore ?? s.highScore ?? s.best ??
          Math.max(bestFromRecent, todayScore, inferredBestFromTotals, 0)
        ),
        // Totals
        totalGames: gamesMaybe,
        totalScore: totalScoreFinal,
        // Average score
        avgScore: (() => {
          if (Number.isFinite(avgMaybe) && avgMaybe > 0) return avgMaybe;
          if (Number.isFinite(totalScoreFinal) && gamesMaybe > 0) {
            return totalScoreFinal / gamesMaybe;
          }
          return 0;
        })(),
        // Last played day (YYYY-MM-DD)
        lastPlayed: (() => {
          const rawLast = s.lastPlayed ?? s.lastPlayedDay ?? s.lastDay ?? s.last;
          if (rawLast) return String(rawLast);

          const lastFromArrays = recent.length ? String(recent[recent.length - 1].day) : null;
          if (lastFromArrays) return lastFromArrays;

          return null;
        })(),
      };

      // Reconcile daysPlayed with recent arrays and localStorage
      try {
        // Unique days in backend recent/last7Days
        const uniqRecentDays = new Set(recentDays).size;
        // Unique days in local history
        const localHistForDays = _loadTriviaHistory(resolvedUid);
        const uniqLocalDays = Array.isArray(localHistForDays)
          ? new Set(localHistForDays.map((r) => r && r.day).filter(Boolean)).size
          : 0;

        // Take max to avoid undercounting
        const totalGamesFinal = Math.max(
          Number(normalized.totalGames || 0),
          Number(uniqRecentDays || 0),
          Number(uniqLocalDays || 0)
        );

        if (Number.isFinite(totalGamesFinal) && totalGamesFinal > Number(normalized.totalGames || 0)) {
          normalized.totalGames = totalGamesFinal;
          // Keep average consistent
          if (Number.isFinite(Number(normalized.totalScore)) && totalGamesFinal > 0) {
            normalized.avgScore = Number(normalized.totalScore) / totalGamesFinal;
          }
        }
      } catch {}

      // Reconcile with local history and backend recent for streak and last day
      try {
        const localHist = _loadTriviaHistory(resolvedUid);
        const localStreak = _computeStreak(localHist);
        const candidates = [Number(normalized.streak || 0), Number(recentStreak || 0), Number(localStreak || 0)];
        const maxStreak = Math.max(...candidates.filter((n) => Number.isFinite(n)));

        if (Number.isFinite(maxStreak)) normalized.streak = maxStreak;

        // Prefer most recent lastPlayed
        const localLast = (Array.isArray(localHist) && localHist.length)
          ? [...new Set(localHist.map((r) => r && r.day).filter(Boolean))].sort().pop()
          : null;

        if (localLast && (!normalized.lastPlayed || String(localLast) > String(normalized.lastPlayed))) {
          normalized.lastPlayed = String(localLast);
        }

        // Use local minimums if backend totalScore/avgScore low
        const sumLocal = (Array.isArray(localHist) ? localHist : []).reduce((acc, r) => acc + (Number(r?.score) || 0), 0);
        if ((!Number.isFinite(normalized.totalScore) || normalized.totalScore <= 0) && sumLocal > 0) {
          normalized.totalScore = sumLocal;
          if (Number(normalized.totalGames) > 0) {
            normalized.avgScore = normalized.totalScore / Number(normalized.totalGames);
          }
        }
      } catch {}

      // Attach normalized recent arrays
      try {
        const attachRecent = Array.isArray(recent) ? recent.filter((x) => x && x.day) : [];
        if (attachRecent.length) {
          normalized.last7Days = attachRecent;
          normalized.recent = attachRecent;
        }
      } catch {}

      // Ensure streak not below inferred
      if (Number.isFinite(recentStreak)) {
        normalized.streak = Math.max(Number(normalized.streak || 0), Number(recentStreak || 0));
      }

      setUserStats(normalized);
      _saveUserStatsCache(resolvedUid, normalized);
    } catch (err) {
      console.error('Error cargando estadísticas de usuario:', err);
      setUserStats(null);
    }
  }, [resolvedUid]);

  return {
    userStats,
    setUserStats,
    fetchUserStats,
  };
};

export default useUserStats;